import React, { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Edit, Trash2, BookOpen, ChevronRight, Sparkles } from "lucide-react";
import axios from "axios";

const ACCOUNT_TYPES = [
  { value: "tillgång", label: "Tillgång (Klass 1)" },
  { value: "eget_kapital", label: "Eget kapital (Klass 2)" },
  { value: "skuld", label: "Skuld (Klass 2)" },
  { value: "intäkt", label: "Intäkt (Klass 3)" },
  { value: "kostnad", label: "Kostnad (Klass 4-8)" },
];

// BAS-kontoplan logik för automatisk kontotyp
const getAccountTypeFromNumber = (accountNumber) => {
  if (!accountNumber || accountNumber.length < 1) return null;
  
  const firstDigit = accountNumber.charAt(0);
  const firstTwo = accountNumber.substring(0, 2);
  
  switch (firstDigit) {
    case "1":
      return { type: "tillgång", label: "Tillgång", description: "Klass 1 - Tillgångar" };
    case "2":
      // 20-29 är Eget kapital, 23-29 är Skulder
      const twoDigit = parseInt(firstTwo);
      if (twoDigit >= 20 && twoDigit <= 22) {
        return { type: "eget_kapital", label: "Eget kapital", description: "Klass 20-22 - Eget kapital" };
      }
      return { type: "skuld", label: "Skuld", description: "Klass 23-29 - Skulder" };
    case "3":
      return { type: "intäkt", label: "Intäkt", description: "Klass 3 - Rörelseintäkter" };
    case "4":
      return { type: "kostnad", label: "Kostnad", description: "Klass 4 - Inköp och varor" };
    case "5":
      return { type: "kostnad", label: "Kostnad", description: "Klass 5 - Övriga externa kostnader" };
    case "6":
      return { type: "kostnad", label: "Kostnad", description: "Klass 6 - Övriga externa kostnader" };
    case "7":
      return { type: "kostnad", label: "Kostnad", description: "Klass 7 - Personalkostnader" };
    case "8":
      // 8xxx är finansiella poster - kan vara både intäkt och kostnad
      const first3 = parseInt(accountNumber.substring(0, 3));
      if (first3 >= 830 && first3 <= 839) {
        return { type: "intäkt", label: "Intäkt", description: "Klass 83 - Ränteintäkter" };
      }
      return { type: "kostnad", label: "Kostnad", description: "Klass 8 - Finansiella poster" };
    default:
      return null;
  }
};

const ChartOfAccounts = () => {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [autoDetectedType, setAutoDetectedType] = useState(null);
  const [formData, setFormData] = useState({
    number: "",
    name: "",
    type: "kostnad",
    description: "",
    parent_number: ""
  });

  // Auto-detect account type when number changes
  const handleAccountNumberChange = (value) => {
    setFormData({ ...formData, number: value });
    
    if (value.length >= 1) {
      const detected = getAccountTypeFromNumber(value);
      if (detected) {
        setAutoDetectedType(detected);
        // Auto-set the type
        setFormData(prev => ({ ...prev, number: value, type: detected.type }));
      } else {
        setAutoDetectedType(null);
      }
    } else {
      setAutoDetectedType(null);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast.error("Kunde inte hämta kontoplan");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await axios.put(`${API}/accounts/${editingAccount.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Konto uppdaterat");
      } else {
        await axios.post(`${API}/accounts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Konto skapat");
      }
      fetchAccounts();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ett fel uppstod");
    }
  };

  const handleDelete = async (account) => {
    if (account.is_system) {
      toast.error("Kan inte ta bort systemkonton");
      return;
    }
    if (!window.confirm(`Ta bort konto ${account.number} - ${account.name}?`)) return;
    
    try {
      await axios.delete(`${API}/accounts/${account.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Konto borttaget");
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort konto");
    }
  };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    setFormData({
      number: account.number,
      name: account.name,
      type: account.type,
      description: account.description || "",
      parent_number: account.parent_number || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAccount(null);
    setAutoDetectedType(null);
    setFormData({
      number: "",
      name: "",
      type: "kostnad",
      description: "",
      parent_number: ""
    });
  };

  // Live search with debounced filtering
  const filteredAccounts = useMemo(() => {
    return accounts.filter(account => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        account.number.toLowerCase().includes(searchLower) ||
        account.name.toLowerCase().includes(searchLower) ||
        (account.description && account.description.toLowerCase().includes(searchLower));
      const matchesType = filterType === "all" || account.type === filterType;
      return matchesSearch && matchesType;
    });
  }, [accounts, searchTerm, filterType]);

  // Search suggestions for live search
  const searchSuggestions = useMemo(() => {
    if (!searchTerm || searchTerm.length < 1) return [];
    const searchLower = searchTerm.toLowerCase();
    return accounts
      .filter(acc => 
        acc.number.toLowerCase().startsWith(searchLower) ||
        acc.name.toLowerCase().includes(searchLower)
      )
      .slice(0, 5)
      .map(acc => ({ number: acc.number, name: acc.name }));
  }, [accounts, searchTerm]);

  // Group accounts by first digit
  const groupedAccounts = filteredAccounts.reduce((acc, account) => {
    const firstDigit = account.number.charAt(0);
    if (!acc[firstDigit]) acc[firstDigit] = [];
    acc[firstDigit].push(account);
    return acc;
  }, {});

  const classNames = {
    "1": "Tillgångar",
    "2": "Eget kapital och skulder",
    "3": "Rörelseintäkter",
    "4": "Inköp och varor",
    "5": "Övriga externa kostnader",
    "6": "Övriga externa kostnader",
    "7": "Personal",
    "8": "Finansiella poster"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="chart-of-accounts">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Kontoplan</h1>
          <p className="text-slate-500 mt-1">BAS-kontoplanen med {accounts.length} konton</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-account-btn">
              <Plus size={16} className="mr-2" />
              Lägg till konto
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingAccount ? "Redigera konto" : "Lägg till konto"}</DialogTitle>
              <DialogDescription>
                {editingAccount ? "Uppdatera kontoinformation" : "Skapa ett nytt konto i kontoplanen"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="number">Kontonummer</Label>
                  <Input
                    id="number"
                    value={formData.number}
                    onChange={(e) => handleAccountNumberChange(e.target.value)}
                    placeholder="1930"
                    required
                    disabled={editingAccount?.is_system}
                    data-testid="account-number-input"
                  />
                  {autoDetectedType && !editingAccount && (
                    <div className="flex items-center gap-2 text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                      <Sparkles size={12} />
                      <span>Auto: {autoDetectedType.description}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Kontotyp</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger data-testid="account-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {autoDetectedType && !editingAccount && (
                    <p className="text-xs text-slate-500">Automatiskt vald baserat på BAS-kontoplan</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Kontonamn</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Företagskonto"
                  required
                  data-testid="account-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beskrivning (valfritt)</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kort beskrivning av kontot"
                  data-testid="account-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="parent_number">Huvudkonto (valfritt)</Label>
                <Input
                  id="parent_number"
                  value={formData.parent_number}
                  onChange={(e) => setFormData({ ...formData, parent_number: e.target.value })}
                  placeholder="1900"
                  data-testid="account-parent-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Avbryt
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-account-btn">
                  {editingAccount ? "Uppdatera" : "Skapa"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" size={18} />
              <Input
                placeholder="Sök konto (nummer eller namn)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-accounts"
              />
              {/* Live search suggestions */}
              {searchTerm && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 overflow-hidden">
                  {searchSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-0"
                      onClick={() => setSearchTerm(suggestion.number)}
                    >
                      <span className="font-mono text-indigo-600 font-medium">{suggestion.number}</span>
                      <span className="text-slate-600 truncate">{suggestion.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[200px]" data-testid="filter-type">
                <SelectValue placeholder="Alla typer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla typer</SelectItem>
                {ACCOUNT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {searchTerm && (
            <p className="text-xs text-slate-500 mt-2">
              Visar {filteredAccounts.length} av {accounts.length} konton
            </p>
          )}
        </CardContent>
      </Card>

      {/* Accounts List */}
      <div className="space-y-6">
        {Object.entries(groupedAccounts).sort(([a], [b]) => a.localeCompare(b)).map(([digit, accs]) => (
          <Card key={digit} className="card-base overflow-hidden" data-testid={`account-class-${digit}`}>
            <CardHeader className="bg-slate-50 border-b border-slate-200 py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BookOpen size={18} className="text-indigo-600" />
                Klass {digit} - {classNames[digit] || "Övriga"}
                <span className="text-sm font-normal text-slate-500">({accs.length} konton)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-24">Konto</th>
                    <th>Namn</th>
                    <th className="w-32">Typ</th>
                    <th className="w-24 text-right">Åtgärder</th>
                  </tr>
                </thead>
                <tbody>
                  {accs.map((account) => (
                    <tr key={account.id} data-testid={`account-row-${account.number}`}>
                      <td>
                        <span className="font-mono font-medium text-slate-900">{account.number}</span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {account.parent_number && (
                            <ChevronRight size={14} className="text-slate-300" />
                          )}
                          <span className={account.parent_number ? "text-slate-600" : "font-medium text-slate-900"}>
                            {account.name}
                          </span>
                          {account.is_system && (
                            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">System</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          account.type === "tillgång" ? "badge-success" :
                          account.type === "skuld" ? "badge-error" :
                          account.type === "intäkt" ? "badge-success" :
                          account.type === "kostnad" ? "badge-warning" :
                          "badge-pending"
                        }`}>
                          {account.type}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(account)}
                            data-testid={`edit-account-${account.number}`}
                          >
                            <Edit size={14} />
                          </Button>
                          {!account.is_system && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(account)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-account-${account.number}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ChartOfAccounts;
